import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

import styles from "./ProcessedDataComponent.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles, faTrash } from "@fortawesome/free-solid-svg-icons";

import { shapeTagForWrite } from "../utils/tagUtils";

/* ----------------------------- Helpers ----------------------------- */
const ALIAS_GROUPS = [
  ["checking", "chequing", "current", "joint"],
  ["savings", "save", "hisave", "high interest", "highinterest"],
  ["credit card", "credit", "visa", "mastercard", "mc", "amex", "american express"],
  ["cash", "wallet"],
  ["loan", "personal loan", "student loan", "auto loan", "car loan"],
  ["mortgage", "home loan"],
  ["investment", "brokerage", "tfsa", "rrsp", "401k", "ira", "stocks"],
];

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Normalize incoming/CSV variants to system enum
const normalizeTypeName = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (["transfer", "transfers", "xfer", "xfers"].includes(s)) return "transfer";
  if (["expense", "expenses", "debit"].includes(s)) return "expense";
  if (["income", "credit", "credits"].includes(s)) return "income";
  if (["refund", "refunds"].includes(s)) return "refund";
  return s || "expense";
};

// UI label (Title Case) from normalized type
const prettyTypeName = (v) => {
  const s = normalizeTypeName(v);
  return s ? s[0].toUpperCase() + s.slice(1) : "";
};

const buildTypeIndex = (accountTypes) => {
  if (!accountTypes) return [];
  return accountTypes.map((t) => {
    const name = t?.name || t?.id || "";
    const combined = `${name} ${t?.id || ""}`;
    const n = norm(combined);
    const tokens = n.split(" ").filter(Boolean);
    const head = tokens[0] || "";
    const groups = new Set();
    ALIAS_GROUPS.forEach((group, gi) => {
      if (group.some((alias) => n.includes(norm(alias)))) groups.add(gi);
    });
    return { t, n, tokens, head, groups: Array.from(groups) };
  });
};

const scoreTypeForAccount = (accName, typeRec) => {
  const a = norm(accName);
  if (!a) return 0;
  const aTokens = new Set(a.split(" ").filter(Boolean));
  const aHead = [...aTokens][0] || "";
  let score = 0;

  if (aHead && typeRec.head) {
    if (typeRec.head.startsWith(aHead) || aHead.startsWith(typeRec.head)) score += 5;
    else if (aHead.slice(0, 3) === typeRec.head.slice(0, 3)) score += 2;
  }
  if (aHead && typeRec.n.includes(aHead)) score += 2;
  if (typeRec.head && a.includes(typeRec.head)) score += 2;

  const shared = typeRec.tokens.filter((t) => aTokens.has(t)).length;
  score += Math.min(shared, 3);

  typeRec.groups.forEach((gi) => {
    const group = ALIAS_GROUPS[gi];
    if (group.some((alias) => a.includes(norm(alias)))) score += 4;
  });

  return score;
};

const guessTypeForAccount = (accName, typeIndex) => {
  if (!typeIndex.length) return null;
  let best = null;
  let bestScore = 0;
  for (const rec of typeIndex) {
    const s = scoreTypeForAccount(accName, rec);
    if (s > bestScore) {
      bestScore = s;
      best = rec.t;
    }
  }
  return bestScore >= 5 ? best : null;
};

const fmt2 = (n) =>
  n == null || !isFinite(n) ? "—" : (Math.round(n * 100) / 100).toFixed(2);

/** Tiny deterministic hash (preview-only IDs) */
const previewId = (parts = []) => {
  const s = parts.join("|");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return String(h);
};

const deriveSide = (t) => {
  const s = String(t?.transferSide || "").toUpperCase();
  if (s === "IN" || s === "OUT") return s;
  const n = Number(t?.amount);
  if (Number.isFinite(n)) return n < 0 ? "OUT" : "IN";
  return null;
};

const normDesc = (d) => norm(d || "").slice(0, 80);

const legGroupKey = (t) => {
  const date = String(t.date || "").slice(0, 10);
  const ccy = String(t.currencyCode || "").toUpperCase();
  const a = Number(t.amount);
  const aAbs = Number.isFinite(a) ? Math.abs(a) : Math.abs(Number(t.amountAbs || 0));
  const descr = normDesc(t.description || "");
  return `${date}|${ccy}|${aAbs}|${descr}`;
};

// Robust matcher for "Balance adjustment" and variants
const isBalanceAdjustment = (desc) => /\bbalance\s*adj(?:ust(?:ment)?)?\b/i.test(String(desc || ""));

/* ---------------------- Transfer grouping for mapping ---------------------- */
const buildTransferGroups = (transactions) => {
  const legsMap = new Map(); // key -> { id, key, legs: {IN,OUT}, members: Set(txId), meta }
  const consolidated = [];   // [{ id, txId, tx, meta }]

  (transactions || []).forEach((t, idx) => {
    if (normalizeTypeName(t?.typeName) !== "transfer") return;
    const txId = String(t.id || t._tmpId || t.tempId || `tx_${idx}`);

    // Consolidated row (single doc with from/to)
    const isConsolidated = !t.transferSide && (t.fromAccountId || t.toAccountId);
    if (isConsolidated) {
      const date = String(t.date || "").slice(0, 10);
      const ccy = String(t.currencyCode || "").toUpperCase();
      const a = Number(t.amountAbs);
      const b = Number(t.amount);
      const amtAbs = Number.isFinite(a) ? Math.abs(a) : (Number.isFinite(b) ? Math.abs(b) : 0);
      const descr = normDesc(t.description || "");
      const syntheticId = t.transferGroupId || `cg_${previewId([date, ccy, String(amtAbs), descr, txId])}`;
      consolidated.push({
        id: syntheticId,
        txId,
        tx: t,
        meta: { date, currencyCode: ccy, amountAbs: amtAbs, description: t.description || "" },
      });
      return;
    }

    // Leg rows
    const groupId = t.transferGroupId || null;
    const key = groupId ? `gid:${groupId}` : `hk:${legGroupKey(t)}`;
    if (!legsMap.has(key)) {
      const kId = groupId || `tg_${previewId([key])}`;
      const date = String(t.date || "").slice(0, 10);
      const ccy = String(t.currencyCode || "").toUpperCase();
      const a = Number(t.amount);
      const aAbs = Number.isFinite(a) ? Math.abs(a) : Math.abs(Number(t.amountAbs || 0));
      legsMap.set(key, {
        id: kId,
        key,
        legs: { IN: null, OUT: null },
        members: new Set(),
        meta: { date, currencyCode: ccy, amountAbs: aAbs, description: t.description || "" },
      });
    }
    const bucket = legsMap.get(key);
    const side = deriveSide(t);
    if (side === "IN" || side === "OUT") {
      if (!bucket.legs[side]) bucket.legs[side] = { ...t, __txKey: txId };
      bucket.members.add(txId);
    } else {
      bucket.members.add(txId);
    }
  });

  const groups = [];

  // Consolidated groups
  for (const cg of consolidated) {
    groups.push({
      id: cg.id,
      kind: "consolidated",
      meta: cg.meta,
      fromAccountId: cg.tx.fromAccountId || null,
      toAccountId: cg.tx.toAccountId || null,
      fromAccountName: cg.tx.fromAccountName || null,
      toAccountName: cg.tx.toAccountName || null,
      amountAbs: cg.meta.amountAbs,
      members: new Set([cg.txId]),
    });
  }

  // Two-leg groups
  for (const [, g] of legsMap) {
    const out = g.legs.OUT;
    const inn = g.legs.IN;
    const fromAccountId = out && out.accountId ? String(out.accountId) : null;
    const toAccountId = inn && inn.accountId ? String(inn.accountId) : null;
    const fromName = out && out.accountName ? out.accountName : null;
    const toName = inn && inn.accountName ? inn.accountName : null;
    groups.push({
      id: g.id,
      kind: "legs",
      meta: g.meta,
      out, // may be null
      in: inn, // may be null
      fromAccountId,
      toAccountId,
      fromAccountName: fromName,
      toAccountName: toName,
      amountAbs: g.meta.amountAbs,
      members: g.members,
    });
  }

  return groups;
};

/* --------------------------------- Component --------------------------------- */
export default function ProcessedDataComponent({
  processed,
  accountTypes,
  accountMapping,
  onMapAccount,
  loadingTypes,
  jsonSummary,
  jsonExamples,
  canWrite,
  onWrite,
  writeState,
  onReplaceTransactions,
}) {
  const { accounts, tags, transactions, counts } = processed;

  const [justWrote, setJustWrote] = useState(false);
  const typeIndex = useMemo(() => buildTypeIndex(accountTypes), [accountTypes]);
  const autoFilledRef = useRef(new Set());
  const attemptedRef = useRef(new Set());

  const [transfersConfirmed, setTransfersConfirmed] = useState(false);

  // Build account options for From/To dropdowns
  const accountOptions = useMemo(() => {
    return (accounts || [])
      .map((a) => ({
        key: a.id || a.slug, // used in select value
        name: a.name || a.slug || "",
        slug: a.slug,
        id: a.id || a.slug, // fallback to slug if no id
      }))
      .filter((o) => o.key && o.name);
  }, [accounts]);

  const accountByKey = useMemo(() => {
    const m = new Map();
    accountOptions.forEach((o) => m.set(String(o.key), o));
    return m;
  }, [accountOptions]);

  const resolveAccByIdOrName = useCallback(
    (idOrName) => {
      if (!idOrName) return null;
      if (accountByKey.has(String(idOrName))) return accountByKey.get(String(idOrName));
      const byName = accountOptions.find((o) => o.name === idOrName);
      return byName || null;
    },
    [accountByKey, accountOptions]
  );

  const accountNameById = useCallback(
    (id) => (id && accountByKey.get(String(id)) ? accountByKey.get(String(id)).name : undefined),
    [accountByKey]
  );

  // Auto-suggest account types
  useEffect(() => {
    if (!accounts?.length || !accountTypes?.length || !onMapAccount) return;
    for (const acc of accounts) {
      const slug = acc.slug;
      if (accountMapping?.[slug]?.id || attemptedRef.current.has(slug)) continue;
      attemptedRef.current.add(slug);
      const suggestion = guessTypeForAccount(acc.name, typeIndex);
      if (suggestion) {
        autoFilledRef.current.add(slug);
        onMapAccount(slug, suggestion);
      }
    }
  }, [accounts, accountTypes, accountMapping, typeIndex, onMapAccount]);

  // Redirect after write
  useEffect(() => {
    if (writeState?.writing) return;
    const done = writeState && /done/i.test(writeState.writeMsg || "");
    if (done) {
      setJustWrote(true);
      const t = setTimeout(() => window.location.assign("/app/dashboard"), 1200);
      return () => clearTimeout(t);
    }
  }, [writeState]);

  const stripKeys = useCallback((val, deny = ["id", "userId"]) => {
    if (Array.isArray(val)) return val.map((v) => stripKeys(v, deny));
    if (val && typeof val === "object") {
      const out = {};
      for (const k in val) {
        if (Object.prototype.hasOwnProperty.call(val, k) && !deny.includes(k)) {
          out[k] = stripKeys(val[k], deny);
        }
      }
      return out;
    }
    return val;
  }, []);

  const builtUserAccounts = useMemo(() => {
    return (accounts || []).map((a) => ({
      name: a.name,
      slug: a.slug,
      currencyCode: a.currencyCode != null ? a.currencyCode : null,
      accountBalance: a.accountBalance != null ? a.accountBalance : 0,
    }));
  }, [accounts]);

  const summaryWithBalances = useMemo(() => {
    const upstream = jsonSummary?.user_accounts || [];
    const upstreamBySlug = new Map(
      upstream.filter((a) => a?.slug).map((a) => [a.slug, a])
    );
    const merged = builtUserAccounts.map((a) => {
      const u = upstreamBySlug.get(a.slug);
      return u
        ? { ...a, ...u, accountBalance: u.accountBalance != null ? u.accountBalance : a.accountBalance }
        : a;
    });
    return { ...jsonSummary, user_accounts: merged.length > 0 ? merged : upstream };
  }, [jsonSummary, builtUserAccounts]);

  const summaryWritePreview = useMemo(() => {
    const normalizedTags = (tags?.nodes || []).map(shapeTagForWrite).filter(Boolean);
    return { ...summaryWithBalances, user_tags: normalizedTags };
  }, [summaryWithBalances, tags?.nodes]);

  const exampleTagDocWriteShape = useMemo(() => {
    const firstTag = (tags?.nodes || [])[0];
    if (!firstTag) return null;
    const shaped = shapeTagForWrite(firstTag);
    if (!shaped) return null;
    const stripped = stripKeys(shaped);
    return {
      ...stripped,
      userEmail: "<user-email>",
      source: "csv-upload",
      createdAt: "<serverTimestamp>",
      updatedAt: "<serverTimestamp>",
    };
  }, [tags?.nodes, stripKeys]);

  /* ---------------- Transfer pairing (singles → transfer) ---------------- */
  const [transferScan, setTransferScan] = useState({ pairs: [], orphans: [], applied: false });

  const suggestTransferPairs = useCallback((list = []) => {
    if (!Array.isArray(list)) return { pairs: [], orphans: [] };
    const groups = new Map(); // key -> {pos:[], neg:[]}
    const keyOf = (t) => {
      const date = String(t.date || "").slice(0, 10);
      const ccy = String(t.currencyCode || "").toUpperCase();
      const amt = Number(t.amount);
      const aAbs = Number.isFinite(amt) ? Math.abs(amt) : 0;
      return `${date}|${ccy}|${aAbs}`;
    };

    list.forEach((t) => {
      const amt = Number(t?.amount);
      if (!Number.isFinite(amt) || amt === 0 || normalizeTypeName(t?.typeName) === "transfer") return;
      const k = keyOf(t);
      if (!groups.has(k)) groups.set(k, { pos: [], neg: [] });
      if (amt > 0) groups.get(k).pos.push(t);
      else groups.get(k).neg.push(t);
    });

    const pairs = [];
    const orphans = [];
    groups.forEach(({ pos, neg }) => {
      const usedPos = new Set();
      const usedNeg = new Set();
      neg.forEach((a) => {
        if (usedNeg.has(a.id)) return;
        const b = pos.find((p) => !usedPos.has(p.id) && String(a.accountId || "") !== String(p.accountId || ""));
        if (b) {
          usedNeg.add(a.id);
          usedPos.add(b.id);
          const bAmt = Number(b.amount);
          const aAmt = Number(a.amount);
          const bAbs = Number.isFinite(bAmt) ? Math.abs(bAmt) : (Number.isFinite(aAmt) ? Math.abs(aAmt) : 0);
          pairs.push({
            from: a,
            to: b,
            amountAbs: bAbs,
            date: a.date || b.date,
            currencyCode: a.currencyCode || b.currencyCode,
            description: a.description || b.description || "",
            tagPath: Array.from(new Set([...(a.tagPath || []), ...(b.tagPath || [])])),
          });
        }
      });
      neg.forEach((r) => {
        if (!usedNeg.has(r.id)) orphans.push(r);
      });
      pos.forEach((r) => {
        if (!usedPos.has(r.id)) orphans.push(r);
      });
    });

    return { pairs, orphans };
  }, []);

  const applySuggestedPairs = useCallback(() => {
    if (!transferScan.pairs?.length) return;
    const toRemove = new Set();
    const consolidated = transferScan.pairs.map((p, idx) => {
      toRemove.add(p.from.id);
      toRemove.add(p.to.id);
      return {
        id: `tmp_tr_${p.from.id}_${p.to.id}_${idx}`, // unique
        typeName: "transfer",
        date: p.date,
        currencyCode: p.currencyCode,
        amountAbs: p.amountAbs,
        fromAccountId: p.from.accountId,
        toAccountId: p.to.accountId,
        fromAccountName: p.from.accountName || p.from.accountId,
        toAccountName: p.to.accountName || p.to.accountId,
        description: p.description,
        tagPath: p.tagPath,
        source: "csv-upload",
      };
    });
    const nextTransactions = (transactions || []).filter((t) => !toRemove.has(t.id)).concat(consolidated);
    onReplaceTransactions?.(nextTransactions);
    setTransferScan((s) => ({ ...s, applied: true }));
    setTransfersConfirmed(false);
  }, [transactions, transferScan, onReplaceTransactions]);

  useEffect(() => {
    const res = suggestTransferPairs(transactions);
    setTransferScan({ ...res, applied: false });
  }, [transactions, suggestTransferPairs]);

  /* ---------------------- Transfer grouping for mapping ---------------------- */
  const transferGroups = useMemo(() => buildTransferGroups(transactions), [transactions]);

  // Build quick maps for tx lookup
  const txByKey = useMemo(() => {
    const m = new Map();
    (transactions || []).forEach((t, i) => {
      const key = String(t.id || t._tmpId || t.tempId || `tx_${i}`);
      m.set(key, t);
    });
    return m;
  }, [transactions]);

  const txGroupByKey = useMemo(() => {
    const m = new Map();
    for (const g of transferGroups) {
      if (!g?.members) continue;
      for (const key of g.members) m.set(String(key), g);
    }
    return m;
  }, [transferGroups]);

  const txKeyOf = useCallback((t) => {
    if (!t) return "";
    const base = t.id || t._tmpId || t.tempId;
    if (base) return String(base);
    const idx = (transactions || []).indexOf(t);
    return `tx_${idx >= 0 ? idx : previewId([t.date || "", t.amount || ""])}`;
  }, [transactions]);

  // Tag utilities
  const uniq = (arr) => Array.from(new Set((arr || []).filter((x) => x != null && x !== "")));

  const mergeTagMeta = (metas) => {
    const out = {
      tagPath: [],
      tagPathIds: [],
      tagAncestorIds: [],
      tagLeafId: undefined,
    };
    for (const m of metas) {
      if (!m) continue;
      if (Array.isArray(m.tagPath)) out.tagPath.push(...m.tagPath);
      if (Array.isArray(m.tagPathIds)) out.tagPathIds.push(...m.tagPathIds);
      if (Array.isArray(m.tagAncestorIds)) out.tagAncestorIds.push(...m.tagAncestorIds);
      if (!out.tagLeafId && m.tagLeafId) out.tagLeafId = m.tagLeafId;
    }
    out.tagPath = uniq(out.tagPath);
    out.tagPathIds = uniq(out.tagPathIds);
    out.tagAncestorIds = uniq(out.tagAncestorIds);
    return out;
  };

  const tagMetaOfTx = (t) => ({
    tagPath: Array.isArray(t?.tagPath)
      ? t.tagPath
      : Array.isArray(t?.tagPaths)
        ? t.tagPaths.map((p) => (Array.isArray(p) ? p.join("/") : String(p)))
        : [],
    tagPathIds: Array.isArray(t?.tagPathIds) ? t.tagPathIds : [],
    tagLeafId: t?.tagLeafId,
    tagAncestorIds: Array.isArray(t?.tagAncestorIds) ? t.tagAncestorIds : [],
  });

  const deriveGroupTagMeta = (g) => {
    const metas = [];
    if (g.kind === "consolidated") {
      const onlyKey = [...(g.members || [])][0];
      const t = txByKey.get(String(onlyKey));
      if (t) metas.push(tagMetaOfTx(t));
    } else {
      if (g.out) metas.push(tagMetaOfTx(g.out));
      if (g.in) metas.push(tagMetaOfTx(g.in));
      // Also inspect any other members just in case
      for (const k of g.members || []) {
        const t = txByKey.get(String(k));
        if (t) metas.push(tagMetaOfTx(t));
      }
    }
    return mergeTagMeta(metas);
  };

  // Update accounts for a group (handles consolidated + two-leg).
  // If the chosen side's leg is missing, synthesize it so the row becomes selectable/completable.
  const setGroupAccount = useCallback(
    (groupId, side /* 'from' | 'to' */, accountKeyVal) => {
      if (!groupId || !accountKeyVal) return;
      const acc = accountByKey.get(String(accountKeyVal));
      if (!acc) return;

      const groups = transferGroups; // snapshot
      const next = (transactions || []).map((t) => t);
      let changed = false;

      const indexOfTxKey = (txKey) =>
        (transactions || []).findIndex(
          (tt, i) => String(tt.id || tt._tmpId || tt.tempId || `tx_${i}`) === String(txKey)
        );

      const upsertLegAccount = (txKey, id, name, groupIdForLeg) => {
        const idx = indexOfTxKey(txKey);
        if (idx < 0) return;
        const prev = next[idx];
        const wantId = String(id || "");
        const prevId = String(prev.accountId || "");
        if (
          prevId === wantId &&
          (prev.accountName || "") === (name || "") &&
          (prev.transferGroupId || "") === (groupIdForLeg || "")
        ) return;
        next[idx] = {
          ...prev,
          accountId: wantId,
          accountName: name || prev.accountName,
          transferGroupId: groupIdForLeg || prev.transferGroupId,
        };
        changed = true;
      };

      const createMissingLeg = (g, legSide /* 'IN' | 'OUT' */, id, name) => {
        const m = g.meta || {};
        const a = Number(m.amountAbs);
        const amountAbs = Number.isFinite(a) ? a : 0;
        const newId = `syn_${g.id}_${legSide}_${previewId([id, m.date || "", m.currencyCode || ""])}`;
        if ((next || []).some((tt) => String(tt.id) === newId)) return;

        const tagMeta = deriveGroupTagMeta(g);

        next.push({
          id: newId,
          typeName: "transfer",
          date: m.date || "",
          currencyCode: m.currencyCode || "",
          amount: legSide === "OUT" ? -amountAbs : amountAbs,
          accountId: String(id),
          accountName: name || undefined,
          description: m.description || "",
          source: "csv-upload",
          transferGroupId: g.id,          // keep both legs in same group
          transferSide: legSide,
          ...tagMeta,
        });
        changed = true;
        return newId;
      };

      const g = groups.find((gg) => gg.id === groupId);
      if (!g) return;
      const isBA = isBalanceAdjustment(g.meta && g.meta.description);

      if (g.kind === "consolidated") {
        // Update the consolidated row
        const txKey = [...g.members][0];
        const idx = indexOfTxKey(txKey);
        if (idx >= 0) {
          const prev = next[idx];
          let upd =
            side === "from"
              ? { ...prev, fromAccountId: acc.id, fromAccountName: acc.name }
              : { ...prev, toAccountId: acc.id, toAccountName: acc.name };

          // Mirror both sides for Balance adjustment
          if (isBA) {
            upd = {
              ...upd,
              fromAccountId: acc.id,
              toAccountId: acc.id,
              fromAccountName: acc.name,
              toAccountName: acc.name,
            };
          }

          if (JSON.stringify(upd) !== JSON.stringify(prev)) {
            next[idx] = upd;
            changed = true;
          }
        }
      } else {
        // Legs group
        const wantSide = side === "from" ? "OUT" : "IN";
        const otherSide = wantSide === "OUT" ? "IN" : "OUT";

        // Ensure the wanted leg exists and is updated
        let wantKey = null;
        if (wantSide === "OUT" && g.out) wantKey = g.out.__txKey;
        if (wantSide === "IN" && g.in) wantKey = g.in.__txKey;
        if (!wantKey) {
          wantKey = createMissingLeg(g, wantSide, acc.id, acc.name);
        } else {
          upsertLegAccount(wantKey, acc.id, acc.name, g.id);
        }

        // For Balance adjustment: mirror the other side too
        if (isBA) {
          let otherKey = null;
          if (otherSide === "OUT" && g.out) otherKey = g.out.__txKey;
          if (otherSide === "IN" && g.in) otherKey = g.in.__txKey;
          if (!otherKey) {
            otherKey = createMissingLeg(g, otherSide, acc.id, acc.name);
          } else {
            upsertLegAccount(otherKey, acc.id, acc.name, g.id);
          }
        } else {
          // Non-BA: if the other leg already exists, at least stamp the same groupId so both legs group together
          const existingOtherKey =
            otherSide === "OUT" ? (g.out && g.out.__txKey) : (g.in && g.in.__txKey);
          if (existingOtherKey) upsertLegAccount(existingOtherKey, undefined, undefined, g.id);
        }
      }

      if (changed) {
        onReplaceTransactions?.(next);
        setTransfersConfirmed(false);
      }
    },
    [transactions, accountByKey, transferGroups, onReplaceTransactions]
  );

  // Delete whole group (both legs or consolidated one)
  const deleteGroup = useCallback(
    (groupId) => {
      if (!groupId) return;
      if (!window.confirm("Delete this transfer from the import? This cannot be undone.")) return;
      const g = transferGroups.find((gg) => gg.id === groupId);
      if (!g) return;
      const next = (transactions || []).filter((t, idx) => {
        const txKey = String(t.id || t._tmpId || t.tempId || `tx_${idx}`);
        return !g.members.has(txKey);
      });
      onReplaceTransactions?.(next);
      setTransfersConfirmed(false);
    },
    [transactions, transferGroups, onReplaceTransactions]
  );

  const confirmTransferMappings = useCallback(() => {
    const incomplete = transferGroups.filter((g) => {
      if (g.kind === "consolidated") return !(g.fromAccountId && g.toAccountId);
      return !(g.fromAccountId && g.toAccountId && g.out && g.in);
    });
    if (incomplete.length) {
      alert(`Please set From and To accounts for all transfers (${incomplete.length} pending).`);
      return;
    }
    setTransfersConfirmed(true);
  }, [transferGroups]);

  /* ------------------ AUTO: Balance-adjustment default mapping ------------------ */
  useEffect(() => {
    if (!transferGroups.length) return;

    const next = (transactions || []).map((t) => t);
    let changed = false;

    const idxByKey = (txKey) =>
      (transactions || []).findIndex(
        (tt, i) => String(tt.id || tt._tmpId || tt.tempId || `tx_${i}`) === String(txKey)
      );

    const setConsolidatedSame = (txKey, id, name) => {
      const idx = idxByKey(txKey);
      if (idx < 0) return;
      const prev = next[idx];
      const upd = {
        ...prev,
        fromAccountId: id,
        toAccountId: id,
        fromAccountName: name || prev.fromAccountName,
        toAccountName: name || prev.toAccountName,
      };
      if (JSON.stringify(upd) !== JSON.stringify(prev)) {
        next[idx] = upd;
        changed = true;
      }
    };

    const upsertLeg = (txKey, id, name, groupId) => {
      const idx = idxByKey(txKey);
      if (idx < 0) return;
      const prev = next[idx];
      const upd = {
        ...prev,
        transferGroupId: groupId || prev.transferGroupId,
      };
      if (id) upd.accountId = String(id);
      if (name) upd.accountName = name;
      if (JSON.stringify(upd) !== JSON.stringify(prev)) {
        next[idx] = upd;
        changed = true;
      }
    };

    const createLeg = (g, legSide, id, name) => {
      const m = g.meta || {};
      const amt = Number(m.amountAbs);
      const amountAbs = Number.isFinite(amt) ? amt : 0;
      const newId = `syn_${g.id}_${legSide}_${previewId([id, m.date || "", m.currencyCode || ""])}`;
      if ((next || []).some((tt) => String(tt.id) === newId)) return;

      const tagMeta = deriveGroupTagMeta(g);

      next.push({
        id: newId,
        typeName: "transfer",
        date: m.date || "",
        currencyCode: m.currencyCode || "",
        amount: legSide === "OUT" ? -amountAbs : amountAbs,
        accountId: String(id),
        accountName: name || undefined,
        description: m.description || "",
        source: "csv-upload",
        transferGroupId: g.id,
        transferSide: legSide,
        ...tagMeta,
      });
      changed = true;
    };

    for (const g of transferGroups) {
      const desc = g.meta && g.meta.description ? g.meta.description : "";
      if (!isBalanceAdjustment(desc)) continue;

      if (g.kind === "consolidated") {
        const txKey = [...g.members][0];
        const chosen = g.fromAccountId || g.toAccountId || null;
        if (!chosen) continue;
        const acc = resolveAccByIdOrName(chosen);
        const name = acc ? acc.name : null;
        setConsolidatedSame(txKey, String(chosen), name);
        continue;
      }

      // legs
      const chosen =
        g.fromAccountId ||
        g.toAccountId ||
        (g.out && g.out.accountId) ||
        (g.in && g.in.accountId) ||
        null;
      if (!chosen) continue;
      const acc = resolveAccByIdOrName(chosen);
      const name = acc ? acc.name : null;

      if (g.out) upsertLeg(g.out.__txKey, chosen, name, g.id);
      else createLeg(g, "OUT", chosen, name);

      if (g.in) upsertLeg(g.in.__txKey, chosen, name, g.id);
      else createLeg(g, "IN", chosen, name);
    }

    if (changed) {
      onReplaceTransactions?.(next);
      setTransfersConfirmed(false);
    }
  }, [transferGroups, transactions, resolveAccByIdOrName, onReplaceTransactions]);

  /* ----------------------------- Write readiness ----------------------------- */
  const allAccountsMapped = useMemo(() => {
    if (!accounts?.length) return true;
    return accounts.every((a) => !!(accountMapping && accountMapping[a.slug] && accountMapping[a.slug].id));
  }, [accounts, accountMapping]);

  const allTransferGroupsMapped = useMemo(() => {
    if (!transferGroups.length) return true;
    return transferGroups.every((g) => {
      if (g.kind === "consolidated") return Boolean(g.fromAccountId && g.toAccountId);
      return Boolean(g.fromAccountId && g.toAccountId && g.out && g.in);
    });
  }, [transferGroups]);

  const readyToWrite = useMemo(() => {
    const transfersOk = !transferGroups.length || (allTransferGroupsMapped && transfersConfirmed);
    return allAccountsMapped && transfersOk;
  }, [allAccountsMapped, allTransferGroupsMapped, transfersConfirmed, transferGroups.length]);

  /* ===================== V2 user_transactions PREVIEW ===================== */
  const previewUserEmail = "<user-email>";

  // Build two legs per transfer group, inheriting tags and names.
  const transferLegDocs = useMemo(() => {
    const docs = [];
    for (const g of transferGroups) {
      // Determine accounts
      const fromId = g.fromAccountId || (g.out && g.out.accountId) || "";
      const toId = g.toAccountId || (g.in && g.in.accountId) || "";
      if (!fromId || !toId) continue; // guard; UI enforces mapping before write

      const fromName = g.fromAccountName || (g.out && g.out.accountName) || accountNameById(fromId);
      const toName = g.toAccountName || (g.in && g.in.accountName) || accountNameById(toId);

      const m = g.meta || {};
      const date = m.date || "";
      const currencyCode = m.currencyCode || "";
      const amountAbs = Number.isFinite(Number(g.amountAbs)) ? Number(g.amountAbs) : Number(m.amountAbs) || 0;
      const groupId = g.id || `tg_${previewId([date, currencyCode, String(amountAbs), fromId, toId])}`;
      const description = m.description || "";

      const tmeta = deriveGroupTagMeta(g);

      const common = {
        userEmail: previewUserEmail,
        date,
        currencyCode,
        description: description || undefined,
        descriptionLower: description ? description.toLowerCase() : undefined,
        source: "csv-upload",
        typeName: "transfer",
        transferGroupId: groupId,
        ...tmeta,
      };

      docs.push({
        id: `out_${previewId([groupId, fromId])}`,
        ...common,
        accountId: String(fromId),
        accountName: fromName,
        amount: -Math.abs(amountAbs),
        transferSide: "OUT",
      });

      docs.push({
        id: `in_${previewId([groupId, toId])}`,
        ...common,
        accountId: String(toId),
        accountName: toName,
        amount: Math.abs(amountAbs),
        transferSide: "IN",
      });
    }
    return docs;
  }, [transferGroups, accountNameById]);

  // Non-transfer passthrough (normalized & resilient)
  const nonTransferDocs = useMemo(() => {
    const list = [];
    for (const t of transactions || []) {
      if (normalizeTypeName(t?.typeName) === "transfer") continue; // handled above
      const amt = Number(t.amount);
      const date = String(t.date || "").slice(0, 10);
      const tp = Array.isArray(t.tagPath)
        ? t.tagPath
        : Array.isArray(t.tagPaths)
          ? t.tagPaths.map((p) => (Array.isArray(p) ? p.join("/") : String(p)))
          : [];

      const typeName = normalizeTypeName(t.typeName);

      list.push({
        id: String(t.id || t._tmpId || t.tempId || previewId([date, t.accountId, amt])),
        userEmail: previewUserEmail,
        userId: t.userId != null ? t.userId : undefined,
        accountId: String(t.accountId != null ? t.accountId : t.account_id != null ? t.account_id : ""),
        accountName: t.accountName != null ? t.accountName : t.account_name != null ? t.account_name : undefined,
        date,
        amount: Number.isFinite(amt) ? amt : 0,
        currencyCode: t.currencyCode || "",
        description: t.description != null ? t.description : undefined,
        descriptionLower: typeof t.description === "string" && t.description.length ? t.description.toLowerCase() : undefined,
        status: t.status != null ? t.status : undefined,
        source: t.source != null ? t.source : "csv-upload",
        sourceRowIndex: t.sourceRowIndex != null ? t.sourceRowIndex : undefined,
        typeName,
        tagPath: tp,
        tagPathIds: t.tagPathIds != null ? t.tagPathIds : undefined,
        tagLeafId: t.tagLeafId != null ? t.tagLeafId : undefined,
        tagAncestorIds: t.tagAncestorIds != null ? t.tagAncestorIds : undefined,
        createdAt: t.createdAt != null ? t.createdAt : undefined,
        updatedAt: t.updatedAt != null ? t.updatedAt : undefined,
      });
    }
    return list;
  }, [transactions]);

  const toV2PreviewTransactions = useMemo(() => {
    // The writer should use this list.
    return [...nonTransferDocs, ...transferLegDocs];
  }, [nonTransferDocs, transferLegDocs]);

  const sampleTransactionDocV2 = useMemo(
    () => (toV2PreviewTransactions.length > 0 ? toV2PreviewTransactions[0] : null),
    [toV2PreviewTransactions]
  );

  /* --------------------------------- Render --------------------------------- */
  const mappedCount = transferGroups.filter((g) =>
    g.kind === "consolidated"
      ? g.fromAccountId && g.toAccountId
      : g.fromAccountId && g.toAccountId && g.out && g.in
  ).length;
  const pendingCount = transferGroups.length - mappedCount;

  return (
    <>
      {/* Accounts */}
      <div className="card shadow-sm my-3">
        <div className="card-header fw-semibold d-flex align-items-center gap-2">
          <span>Accounts ({counts.accounts})</span>
          <span className="small text-secondary">(auto-suggests a type from name; please review)</span>
        </div>
        <div className="card-body">
          {!loadingTypes && accountTypes.length === 0 && (
            <div className="alert alert-warning py-2 small">
              No system account types found. Check collection <code>pp_account_types</code> and Firestore rules.
            </div>
          )}

          {accounts.length === 0 ? (
            <div className="text-muted">No accounts detected.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Account name</th>
                    <th>Currency</th>
                    <th className="text-end">Balance</th>
                    <th style={{ width: 360 }}>Map to system account type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a, rowIdx) => {
                    const mapped = accountMapping[a.slug] || null;
                    const wasSuggested = autoFilledRef.current.has(a.slug);
                    return (
                      <tr key={`${a.slug || a.id || "acc"}_${rowIdx}`}>
                        <td>
                          <code>{a.name}</code>
                          {wasSuggested && (
                            <FontAwesomeIcon
                              icon={faWandMagicSparkles}
                              className="ms-2"
                              title="Auto-suggested"
                              style={{ opacity: 0.7 }}
                            />
                          )}
                        </td>
                        <td>{a.currencyCode || <span className="text-secondary">—</span>}</td>
                        <td className="text-end">
                          <span>{fmt2(a.accountBalance)}</span>
                          <span className="text-secondary ms-1">{a.currencyCode || ""}</span>
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={(mapped && mapped.id) || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              const obj = accountTypes.find((t) => t.id === val) || null;
                              autoFilledRef.current.delete(a.slug);
                              onMapAccount?.(a.slug, obj);
                            }}
                          >
                            <option value="">{loadingTypes ? "Loading..." : "Select a type…"}</option>
                            {accountTypes.map((t, i) => (
                              <option key={`${t.id || t.name || "type"}_${i}`} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="small text-muted">
                          {mapped?.desc || <span className="text-secondary">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="card shadow-sm mb-3">
        <div className="card-header fw-semibold">Tags ({counts.tags})</div>
        <div className="card-body">
          {tags.nodes.length === 0 ? (
            <div className="text-muted">No tags detected.</div>
          ) : (
            <>
              <div className="mb-3">
                <div className="fw-semibold">Unique Tag Nodes</div>
                <div className="small text-muted">Each tag is unique regardless of hierarchy.</div>
                <div className={`${styles.scrollPane} mt-2`}>
                  {tags.nodes.map((n, i) => (
                    <span key={`${n.slug}_${i}`} className="badge text-bg-light me-2 mb-2">
                      <code>{n.name}</code>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="fw-semibold">Hierarchy Preview</div>
                <div className="small text-muted">Relationships preserved (parent → child).</div>
                <div className={styles.scrollPane}>
                  <TagTree roots={tags.tree} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Transfer account mapping (grouped) */}
      {transferGroups.length > 0 && (
        <div className="card shadow-sm mb-3">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span className="fw-semibold">
              Transfer account mapping{" "}
              <span className="text-muted">({transferGroups.length})</span>
            </span>
            <div className="d-flex align-items-center gap-2 small">
              <span className="badge bg-success">{mappedCount} mapped</span>
              <span className="badge bg-warning text-dark">{pendingCount} pending</span>
            </div>
          </div>
          <div className="card-body">
            <div style={{ maxHeight: 360, overflow: "auto" }}>
              <table className="table table-sm align-middle">
                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: "10%" }}>Date</th>
                    <th>Description</th>
                    <th style={{ width: "22%" }}>− From (source)</th>
                    <th style={{ width: "22%" }}>+ To (destination)</th>
                    <th style={{ width: "10%" }} className="text-end">Amount</th>
                    <th style={{ width: "10%" }}>Status</th>
                    <th style={{ width: "6rem" }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transferGroups.map((g, i) => {
                    const rowKey = `${g.id}__${i}`;
                    const isComplete =
                      g.kind === "consolidated"
                        ? Boolean(g.fromAccountId && g.toAccountId)
                        : Boolean(g.fromAccountId && g.toAccountId && g.out && g.in);
                    const rowTint = isComplete ? "rgba(25,135,84,.06)" : "rgba(255,193,7,.10)";
                    const date = g.meta.date || "";
                    const descr = g.meta.description || "—";
                    const amt = g.amountAbs != null ? g.amountAbs : g.meta.amountAbs;

                    return (
                      <tr key={rowKey} style={{ backgroundColor: rowTint }}>
                        <td className="is-mono">{date}</td>
                        <td className="text-truncate" title={descr}>{descr}</td>

                        {/* − From */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-secondary" title="Source account">−</span>
                            <select
                              className={`form-select form-select-sm ${!g.fromAccountId ? "is-invalid" : ""}`}
                              value={g.fromAccountId ? String(g.fromAccountId) : ""}
                              onChange={(e) => setGroupAccount(g.id, "from", e.target.value)}
                              aria-label="From account"
                            >
                              <option value="">{g.fromAccountName || "Select From…"}</option>
                              {accountOptions.map((o, j) => (
                                <option key={`from_${rowKey}_${o.key}_${j}`} value={String(o.key)}>
                                  {o.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>

                        {/* + To */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-secondary" title="Destination account">+</span>
                            <select
                              className={`form-select form-select-sm ${!g.toAccountId ? "is-invalid" : ""}`}
                              value={g.toAccountId ? String(g.toAccountId) : ""}
                              onChange={(e) => setGroupAccount(g.id, "to", e.target.value)}
                              aria-label="To account"
                            >
                              <option value="">{g.toAccountName || "Select To…"}</option>
                              {accountOptions.map((o, j) => (
                                <option key={`to_${rowKey}_${o.key}_${j}`} value={String(o.key)}>
                                  {o.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>

                        <td className="text-end is-mono">{fmt2(amt)}</td>

                        <td>
                          {isComplete ? (
                            <span className="badge bg-success">Ready</span>
                          ) : (
                            <span className="badge bg-warning text-dark">Pending</span>
                          )}
                        </td>

                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            title="Delete this transfer (both legs)"
                            onClick={() => deleteGroup(g.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end pt-2 gap-2">
              <button type="button" className="btn btn-primary" onClick={confirmTransferMappings}>
                Confirm transfer mappings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transactions (UI sample rows) */}
      <div className="card shadow-sm mb-3">
        <div className="card-header fw-semibold">Transactions (sample)</div>
        <div className="card-body">
          {!transfersConfirmed && transferGroups.length > 0 ? (
            <div className="alert alert-info small mb-0">
              Please complete and <strong>Confirm transfer mappings</strong> above to view the sample transactions.
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-muted">No transactions detected.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Account / From → To</th>
                    <th>Type</th>
                    <th>Currency</th>
                    <th className="text-end">Amount</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 10).map((t, i) => {
                    const isTransfer = normalizeTypeName(t?.typeName) === "transfer";
                    const a = Number(t.amountAbs);
                    const b = Number(t.amount);
                    const absForDisplay = Number.isFinite(a) ? a : (Number.isFinite(b) ? Math.abs(b) : 0);
                    const amountForDisplay = isTransfer ? absForDisplay : (Number.isFinite(b) ? b : 0);
                    const tagPath =
                      Array.isArray(t.tagPaths) && t.tagPaths.length
                        ? t.tagPaths.join(" | ")
                        : Array.isArray(t.tagPath) && t.tagPath.length
                          ? t.tagPath.join(" / ")
                          : "—";

                    // Pull mapped From/To from the group when row doesn't have both fields
                    const rowTxKey = txKeyOf(t);
                    let fromDisp = t.fromAccountName || t.fromAccountId || "";
                    let toDisp   = t.toAccountName   || t.toAccountId   || "";
                    if (isTransfer && (!fromDisp || !toDisp)) {
                      const g = txGroupByKey.get(rowTxKey);
                      if (g) {
                        if (!fromDisp) fromDisp = g.fromAccountName || g.fromAccountId || "";
                        if (!toDisp)   toDisp   = g.toAccountName   || g.toAccountId   || "";
                      }
                    }

                    const txKey = `${t.id || t._tmpId || t.tempId || "row"}_${i}`;
                    return (
                      <tr key={txKey}>
                        <td><code>{t.date}</code></td>
                        <td>{t.description}</td>
                        <td>
                          {isTransfer ? (
                            <span>
                              <code>{fromDisp || "?"}</code>
                              <span className="mx-1">→</span>
                              <code>{toDisp || "?"}</code>
                            </span>
                          ) : (
                            <code>{t.accountName}</code>
                          )}
                        </td>
                        <td>{prettyTypeName(t.typeName)}</td>
                        <td>{t.currencyCode}</td>
                        <td className="text-end">{fmt2(amountForDisplay)}</td>
                        <td><code>{tagPath}</code></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* JSON summary (V2 preview) */}
      <div className="card shadow-sm mb-3">
        <div className="card-header d-flex align-items-center justify-content-between">
          <span className="fw-semibold">JSON summary (preview)</span>
          <span className="small text-secondary">
            Tags below are shown in the exact write format (normalized <code>fullPath</code>, leaf <code>name</code>, <code>slug</code>).
          </span>
        </div>
        <div className="card-body">
          <SummaryTabs
            jsonSummary={{
              ...summaryWithBalances,
              user_tags: summaryWritePreview.user_tags,
              user_transactions: toV2PreviewTransactions,
            }}
            jsonExamples={{
              user_accounts: stripKeys(jsonExamples?.user_accounts),
              user_tags: stripKeys(exampleTagDocWriteShape),
              user_transactions: stripKeys(sampleTransactionDocV2),
            }}
          />
        </div>
      </div>

      {/* Write */}
      <div className="d-flex gap-2 align-items-center">
        <button
          className="btn btn-success"
          onClick={onWrite}
          disabled={!canWrite || !readyToWrite || writeState?.writing}
          title={!readyToWrite ? "Finish mapping accounts and transfer From/To (and confirm) before writing." : undefined}
        >
          {writeState?.writing ? "Writing…" : `Write to Firebase (${counts.transactions})`}
        </button>
        {writeState?.writeMsg && <div className="small text-secondary">{writeState.writeMsg}</div>}
      </div>

      {justWrote && (
        <div className="alert alert-success d-inline-flex align-items-center gap-2 mt-3 py-2">
          <span className="fw-semibold">Import complete.</span>
          <span className="small">Redirecting to your dashboard…</span>
        </div>
      )}
    </>
  );
}

/* ------------------------------- Subcomponents ------------------------------- */
function TagTree({ roots }) {
  if (!roots || roots.length === 0) return <div className="text-muted">No tags</div>;
  return (
    <ul className="mb-0">
      {roots.map((n, i) => (
        <li key={`${n.slug || n.name || "tag"}_${i}`}>
          <code>{n.name}</code>
          {Array.isArray(n.children) && n.children.length > 0 && <TagTree roots={n.children} />}
        </li>
      ))}
    </ul>
  );
}

function SummaryTabs({ jsonSummary, jsonExamples }) {
  const [tab, setTab] = useState("accounts");
  const { ua, ut, ux } = useMemo(
    () => ({
      ua: jsonSummary?.user_accounts || [],
      ut: jsonSummary?.user_tags || [],
      ux: jsonSummary?.user_transactions || [],
    }),
    [jsonSummary]
  );

  const PREVIEW = 200;
  const { show, example, count } = useMemo(() => {
    switch (tab) {
      case "accounts":
        return { show: ua.slice(0, PREVIEW), example: jsonExamples?.user_accounts || null, count: ua.length };
      case "tags":
        return { show: ut.slice(0, PREVIEW), example: jsonExamples?.user_tags || null, count: ut.length };
      case "transactions":
        return { show: ux.slice(0, PREVIEW), example: jsonExamples?.user_transactions || null, count: ux.length };
      default:
        return { show: [], example: null, count: 0 };
    }
  }, [tab, ua, ut, ux, jsonExamples]);

  return (
    <div>
      <div className={`nav nav-tabs ${styles.tabNav}`} role="tablist">
        <button className={`nav-link ${tab === "accounts" ? "active" : ""}`} onClick={() => setTab("accounts")} type="button">
          user_accounts <span className="text-muted">({ua.length})</span>
        </button>
        <button className={`nav-link ${tab === "tags" ? "active" : ""}`} onClick={() => setTab("tags")} type="button">
          user_tags <span className="text-muted">({ut.length})</span>
        </button>
        <button className={`nav-link ${tab === "transactions" ? "active" : ""}`} onClick={() => setTab("transactions")} type="button">
          user_transactions <span className="text-muted">({ux.length})</span>
        </button>
      </div>

      <CollapsibleSample title="Sample document (full shape)" json={example} emptyText="No sample available." />

      <div className={styles.jsonPane}>
        <div className="d-flex justify-content-between small text-secondary mb-2">
          <span>Showing {show.length} of {count}</span>
        </div>
        <JsonPretty className={`${styles.jsonPre} small mb-0`} value={show} />
      </div>
    </div>
  );
}

function CollapsibleSample({ title, json, emptyText = "No content." }) {
  const [open, setOpen] = useState(false);
  const handleToggle = useCallback(() => setOpen((o) => !o), []);
  return (
    <div className={styles.collapseCard}>
      <button type="button" className={styles.collapseHeader} onClick={handleToggle} aria-expanded={open}>
        <span className={styles.chevron} data-open={open ? "1" : "0"}>▸</span>
        <span className="fw-semibold">{title}</span>
        <span className="ms-auto small text-secondary">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className={styles.collapseBody}>
          {json ? (
            <JsonPretty className={`${styles.jsonPre} small mb-0`} value={json} />
          ) : (
            <pre className={`${styles.jsonPre} small mb-0`}>{emptyText}</pre>
          )}
        </div>
      )}
    </div>
  );
}

const JsonPretty = React.memo(({ value, className = "" }) => (
  <pre className={className}>
    <JsonNode value={value} />
  </pre>
));

const JsonNode = React.memo(({ value, depth = 0 }) => {
  const indent = "  ".repeat(depth);
  const nextIndent = "  ".repeat(depth + 1);
  if (value === null) return <span className="jsonNull">null</span>;
  const t = typeof value;
  if (t === "string") return <span className="jsonString">{JSON.stringify(value)}</span>;
  if (t === "number") return <span className="jsonNumber">{String(value)}</span>;
  if (t === "boolean") return <span className="jsonBoolean">{value ? "true" : "false"}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span>[]</span>;
    return (
      <>
        [{"\n"}
        {value.map((v, i) => (
          <React.Fragment key={`arr_${depth}_${i}`}>
            {nextIndent}
            <JsonNode value={v} depth={depth + 1} />
            {i === value.length - 1 ? "" : ","}
            {"\n"}
          </React.Fragment>
        ))}
        {indent}]
      </>
    );
  }

  const keys = Object.keys(value || {});
  if (keys.length === 0) return <span>{"{}"}</span>;
  return (
    <>
      {"{"}
      {"\n"}
      {keys.map((k, i) => (
        <React.Fragment key={`obj_${depth}_${k}_${i}`}>
          {nextIndent}
          <span className="jsonKey">{JSON.stringify(k)}</span>
          {": "}
          <JsonNode value={value[k]} depth={depth + 1} />
          {i === keys.length - 1 ? "" : ","}
          {"\n"}
        </React.Fragment>
      ))}
      {indent}
      {"}"}
    </>
  );
});
